export interface MaintenanceStatus {
  status: 'operational' | 'maintenance';
  maintenance: {
    active: boolean;
    mode: 'OFF' | 'READ_ONLY' | 'SURVEY_ONLY' | 'FULL';
    message: string;
    expectedEndTime?: string;
  };
  timestamp: string;
  version: string;
}

class MaintenanceService {
  private statusCheckInterval: NodeJS.Timeout | null = null;
  private lastStatus: MaintenanceStatus | null = null;
  private statusCallbacks: Set<(status: MaintenanceStatus) => void> = new Set();

  private apiBaseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '') || 'http://localhost:3501';

  async checkStatus(): Promise<MaintenanceStatus> {
    try {
      const response = await fetch(`${(this.apiBaseUrl)}/api/status`);
      if (response.ok) {
        const status = await response.json();
        this.lastStatus = status;
        this.notifyCallbacks(status);
        return status;
      }
      
      // If we get a 503, parse the maintenance response
      if (response.status === 503) {
        const errorData = await response.json();
        const status: MaintenanceStatus = {
          status: 'maintenance',
          maintenance: {
            active: true,
            mode: errorData.maintenanceMode || 'FULL',
            message: errorData.message || 'System is under maintenance',
            expectedEndTime: errorData.expectedEndTime,
          },
          timestamp: errorData.timestamp || new Date().toISOString(),
          version: '1.0.0',
        };
        this.lastStatus = status;
        this.notifyCallbacks(status);
        return status;
      }
    } catch (error) {
      console.error('Failed to check maintenance status:', error);
    }

    // Default to operational if we can't reach the status endpoint
    return {
      status: 'operational',
      maintenance: {
        active: false,
        mode: 'OFF',
        message: '',
      },
      timestamp: new Date().toISOString(),
      version: '1.0.0',
    };
  }

  startPolling(intervalMs: number = 30000): void {
    this.stopPolling();
    
    // Initial check
    this.checkStatus();
    
    // Set up interval
    this.statusCheckInterval = setInterval(() => {
      this.checkStatus();
    }, intervalMs);
  }

  stopPolling(): void {
    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
      this.statusCheckInterval = null;
    }
  }

  onStatusChange(callback: (status: MaintenanceStatus) => void): () => void {
    this.statusCallbacks.add(callback);
    
    // If we have a last status, immediately call the callback
    if (this.lastStatus) {
      callback(this.lastStatus);
    }
    
    // Return unsubscribe function
    return () => {
      this.statusCallbacks.delete(callback);
    };
  }

  private notifyCallbacks(status: MaintenanceStatus): void {
    this.statusCallbacks.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('Error in maintenance status callback:', error);
      }
    });
  }

  getLastStatus(): MaintenanceStatus | null {
    return this.lastStatus;
  }

  isReadOnly(): boolean {
    return this.lastStatus?.maintenance.mode === 'READ_ONLY';
  }

  isSurveyOnly(): boolean {
    return this.lastStatus?.maintenance.mode === 'SURVEY_ONLY';
  }

  isFullMaintenance(): boolean {
    return this.lastStatus?.maintenance.mode === 'FULL';
  }

  isOperational(): boolean {
    return !this.lastStatus?.maintenance.active;
  }
}

export const maintenanceService = new MaintenanceService();
export default maintenanceService;